import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { generateExperienceCheckoutTicket } from 'lib/checkout/experiences/cart'
import { generateFlightCheckoutItem } from 'lib/checkout/flights/cart'
import { omitKeys } from 'lib/object/objectUtils'
import { Exact, oneOf, optional, validate, ValidatedType } from 'lib/validate/simpleValidator'
import uuidV4 from 'lib/string/uuidV4Utils'
import moment from 'moment'
import { generatePassengersFromOccupants } from '../flights/passenger'
import { FlightViewTypes } from 'constants/flight'

export type RestoreCartProductType = 'experience' // Add other product types here as a union when they are implemented

type Tickets = Array<{ ticketId: string, fareType: string, name: string, count: number }>

export function encodeExperienceTickets(tickets: Tickets): string {
  return tickets.map(t => `${t.ticketId}:${t.count}:${encodeURIComponent(t.name)}:${encodeURIComponent(t.fareType)}`).join(',')
}

export function decodeExperienceTickets(str: string): Tickets {
  return str.split(',').map(t => {
    const [ticketId, countStr, name, fareType] = t.split(':')
    const count = +countStr
    if (!ticketId || !name || !fareType || isNaN(count)) {
      throw new Error('Invalid ticket format')
    }
    return { ticketId, count, name: decodeURIComponent(name), fareType: decodeURIComponent(fareType) }
  })
}

export function validateArray(itemsParam: unknown) {
  if (
    !Array.isArray(itemsParam) ||
    itemsParam.length === 0 ||
    itemsParam.some(item => typeof item !== 'object')
  ) { throw new Error('Error parsing items') }
}

export function parseCheckoutItems(itemsParam: any[]): Array<App.Checkout.Item> {
  const parsedItems = itemsParam.flatMap(item => {
    if (item.itemType === 'hotel') {
      return parseHotelItem(item)
    } else if (item.itemType === 'bedbankHotel') {
      return parseBedbankHotelItem(item)
    } else if (item.itemType === 'experience') {
      return parseExperienceItem(item)
    } else {
      throw new Error('Unsupported item type')
    }
  })

  return parsedItems
}

// The schemas below constitute a public API, so don't go changing them willy nilly

const occupantsSchema = {
  roomId: 'string?',
  adults: 'number',
  children: 'number?',
  infants: 'number?',
  childrenAge: optional(['number']),
} as const

export const leHotelSchema = {
  itemType: '"hotel"',
  packageId: 'string',
  /** @deprecated - don't remove or it will break old restore carts */
  roomTypeId: 'string?',
  roomRateId: 'string',
  offerId: 'string',
  duration: 'number',
  isBundled: 'boolean?',
  flightBasePrice: 'number?',
  flightOriginCode: 'string?',
  total: 'number?',
  reservationType: oneOf(['"instant_booking"', '"buy_now_book_later"']),
} as const

export const bnblHotelSchema = {
  [Exact]: true,
  ...leHotelSchema,
  reservationType: '"buy_now_book_later"',
} as const
export const instantBookingSchema = {
  [Exact]: true,
  ...leHotelSchema,
  reservationType: '"instant_booking"',
  checkIn: 'string',
  checkOut: 'string',
  occupancy: occupantsSchema,
} as const
export type ValidHotelInstantBookingCheckoutSchema = ValidatedType<typeof instantBookingSchema>

const deprecatedHotelFields = ['roomTypeId', 'isBundled', 'flightBasePrice', 'flightOriginCode'] as const

function parseHotelItem(item: { itemType: 'hotel' }): Array<App.Checkout.Item> {
  if (validate(instantBookingSchema, item)) {
    const hotelItem: App.Checkout.InstantBookingLEHotelItem = {
      itemId: uuidV4(),
      transactionKey: uuidV4(),
      ...omitKeys(deprecatedHotelFields, item),
    }

    if (item.isBundled) {
      const flightItem: App.Checkout.FlightItem = generateFlightCheckoutItem({
        originAirportCode: item.flightOriginCode!,
        passengers: generatePassengersFromOccupants(item.occupancy),
        viewType: FlightViewTypes.TWO_ONE_WAYS,
        bundledItemIds: [hotelItem.itemId],
        fareType: 'return',
        occupants: item.occupancy,
        quotedFare: item.flightBasePrice,
      })

      return [
        hotelItem,
        flightItem,
      ]
    }

    return [hotelItem]
  } else if (validate(bnblHotelSchema, item)) {
    const parsedItem: App.Checkout.BNBLLEHotelItem = {
      itemId: uuidV4(),
      transactionKey: uuidV4(),
      ...omitKeys(deprecatedHotelFields, item),
    }
    return [parsedItem]
  }
  throw new Error('Invalid item format')
}

export const bedbankHotelSchema = {
  itemType: '"bedbankHotel"',
  offerId: 'string',
  duration: 'number',
  /** @deprecated - don't remove or it will break old restore carts */
  sessionId: 'string?',
  roomId: 'string',
  checkIn: 'string',
  checkOut: 'string',
  roomRateId: 'string',
  /** If not provided, it will default to the first bed group for the rate */
  bedGroupId: 'string?',
  occupancy: occupantsSchema,
  isBundled: 'boolean?',
  flightBasePrice: 'number?',
  flightOriginCode: 'string?',
  total: 'number?',
} as const
export type ValidBedbankCheckoutSchema = ValidatedType<typeof bedbankHotelSchema>

function parseBedbankHotelItem(item: { itemType: 'bedbankHotel' }): Array<App.Checkout.Item> {
  if (!validate(bedbankHotelSchema, item)) { throw new Error('Invalid item format') }
  const hotelItem: App.Checkout.BedbankHotelItem = {
    itemId: uuidV4(),
    transactionKey: uuidV4(),
    // These two fields must be filled in after parsing
    sessionId: '',
    bedGroupId: '',
    ...item,
  }

  if (item.isBundled) {
    const flightItem: App.Checkout.FlightItem = generateFlightCheckoutItem({
      originAirportCode: item.flightOriginCode!,
      passengers: generatePassengersFromOccupants(item.occupancy),
      viewType: FlightViewTypes.TWO_ONE_WAYS,
      bundledItemIds: [hotelItem.itemId],
      fareType: 'return',
      occupants: item.occupancy,
      quotedFare: item.flightBasePrice,
    })

    return [
      hotelItem,
      flightItem,
    ]
  }

  return [hotelItem]
}

export const experienceSchema = {
  [Exact]: true,
  itemType: '"experience"',
  offerType: optional(oneOf(['"channel_manager"', '"always_on"', '"flash"', '"addon"'])),
  experienceId: 'string',
  tickets: [{
    ticketId: 'string',
    name: 'string',
    fareType: 'string?',
    count: 'number',
    unavailable: 'boolean?',
  }],
  date: 'string?',
  languageId: 'string?',
  time: 'string?',
  pickupPointId: 'string?',
  redemptionLocationId: 'string?',
  leExclusive: 'boolean',
  addon: optional({
    offerId: 'string',
    packageId: 'string',
  }),
  isBundled: 'boolean?',
  flightBasePrice: 'number?',
  flightOriginCode: 'string?',
  isBuyNowBookLater: 'boolean?',
  isGift: 'boolean?',
  total: 'number?',
} as const
export type ValidExperienceCheckoutSchema = ValidatedType<typeof experienceSchema>

function parseExperienceItem(item: { itemType: 'experience' }): App.Checkout.ExperienceItem {
  if (!validate(experienceSchema, item)) { throw new Error('Invalid item format') }
  const parsedItem: App.Checkout.ExperienceItem = {
    itemId: uuidV4(),
    transactionKey: uuidV4(),
    ...item,
    isBuyNowBookLater: item.isBuyNowBookLater ?? false,
    date: item.date ?? moment().format(ISO_DATE_FORMAT),
    isGift: !!item.isGift,
  }
  return parsedItem
}

export function replaceTicketsByFareType(item: App.Checkout.ExperienceItem, timeslot: App.ExperienceAvailabilityTimeSlot): App.Checkout.ExperienceItem {
  const fixedTickets = item.tickets.map(cartTicket => {
    let ticket = timeslot.tickets.find(t => t.id === cartTicket.ticketId)
    if (!ticket && cartTicket.fareType) {
      ticket = timeslot.tickets.find(t => t.fareType === cartTicket.fareType)
    }
    if (!ticket) { return cartTicket }
    return generateExperienceCheckoutTicket({ ticket, count: cartTicket.count })
  })

  return { ...item, tickets: fixedTickets }
}

export function fillItemKeyAndTransactionKey(item: App.Checkout.Item) {
  return {
    ...item,
    itemId: item.itemId ?? uuidV4(),
    transactionKey: item.transactionKey ?? uuidV4(),
  }
}

export function getRestoreCartUri(items: App.Checkout.Item[], validateSchema: boolean = true) {
  const itemsForRestoreCart = items.map(item => {
    const { itemId, transactionKey, ...rest } = item
    return rest
  })
  const encodedURI = encodeURIComponent(JSON.stringify(itemsForRestoreCart))
  let uri = `/checkout-restore-cart?items=${encodedURI}`
  if (!validateSchema) {
    uri += '&parseItems=false'
  }
  return uri
}
